import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { Card, List, Typography, Notification, Spin } from "@arco-design/web-react";
import dayjs from "dayjs";

import { createPostRequest, fetchPosts } from "../../../../api/feed";
import { CreatePostModal, Post } from "./components";
import {  PostApi } from "./types/types";
import { LoggedUser } from "../../../../models/Person";

import "./Feed.css";
import { useTranslation } from "react-i18next";
import { handleFeedApiError } from "../../../../api/utils/handleFeedApiError";
import { trackEvent } from "../../../../analytics/track";

interface IFeedProps {
  user: LoggedUser | null;
  titleKey?: string;
}

const fetchPostsQueryKey = "posts";

export interface PostsResponse {
  posts: PostApi[];
  count: number;
}

export const Feed = ({ user, titleKey = "home_page.feed" }: IFeedProps) => {
  const { t } = useTranslation();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useInfiniteQuery({
      queryKey: [fetchPostsQueryKey],
      initialPageParam: 0,
      queryFn: ({ pageParam }) => fetchPosts(pageParam),

      getNextPageParam: (lastPage, pages) => {
        if (lastPage.count < 10) return undefined;
        return pages.length * 10;
      },
      refetchOnWindowFocus: true,
    });

  const posts = data?.pages.flatMap((page) => page.posts);

  const handleReachBottom = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const { mutateAsync: createPost, isPending: isPostCreating } = useMutation({
    mutationFn: createPostRequest,
    onSuccess: (createdPost) => {
      refetch();
      if (createdPost?.status === "scheduled" && createdPost.publish_at) {
        Notification.success({
          content: t("notification_messages.post_scheduled_success", {
            datetime: dayjs(createdPost.publish_at).format("MMM D, YYYY h:mm A"),
          }),
        });
        trackEvent("create_post", { status: "scheduled" });
        return;
      }
      Notification.success({
        content: t("notification_messages.post_created_success"),
      });
      trackEvent("create_post", { status: "published" });
    },
    onError: (error) => {
      handleFeedApiError(error, t);
    },
  });

  const handleCreatePost = (newPost: FormData) => {
    createPost(newPost);
  };

  const renderNoDataElement = () => (
    <span className="posts-list__no-data">
      {t('home_page.no_posts')}
    </span>
  );

  return (
    <Card className="own-card feed-panel__card" style={{ marginTop: titleKey === "home_page.community_feed" ? 0 : 20 }}>
      <div className="card-header">
        <Typography.Title heading={3} className="card-header__title">
          {t(titleKey)}
        </Typography.Title>
        <CreatePostModal user={user} onCreatePost={handleCreatePost} isPostCreating={isPostCreating}/>
      </div>
      <List
        className="posts-list"
        scrollLoading={hasNextPage && <Spin loading={isFetchingNextPage} />}
        onReachBottom={handleReachBottom}
        dataSource={posts}
        noDataElement={renderNoDataElement()}
        render={(item) => user && (
          <Post
            key={item.id}
            post={item}
            currentUser={user}
            refetch={refetch}
          />)
        }
      />
    </Card>
  );
};
